import { Box } from '@mentimeter/ragnar-ui/box';
import type { BulletsStaticContent } from '@core-api/presentation/types/response';
import {
  usePresentationContext,
  usePresentationSlideContext,
} from '../../legacy-context/presentationContext';
import { getNinePointLayoutProperties } from '../../shared/utils/getNinePointLayoutProperties';
import { ContentSlideTitleSection } from '../../shared/components/ContentSlideTitleSection';
import { StaggerCss } from '../../shared/components/StaggerCss';
import { getLegacyLayout } from '../../utils/legacyContentSlideUtils/getLegacyLayout';
import { Steps } from './Steps';
import fontOffset from './fontOffset';

export function BulletsPresentation() {
  const { useConfig, usePace } = usePresentationContext();

  const slide = usePresentationSlideContext();
  const staticContent = slide.staticContent as BulletsStaticContent;
  const title = staticContent.heading;
  const fontSizeOffset = staticContent.textSizeOffset;
  const legacyLayout = getLegacyLayout(slide) ?? {
    type: 'default',
    font_size_offset: 0,
    alignment_x: 1,
    alignment_y: 1,
    size: '25%',
  };
  const choices = staticContent.list.items.map(({ id, content }, index) => ({
    id: id as unknown as number,
    label: content,
    correct_answer: false,
    position: index + 1,
  }));
  const hasImage = slide.images?.length > 0;

  const { step } = usePace();
  const { textAlign, justifyContent, alignItems } =
    getNinePointLayoutProperties(legacyLayout);
  const { mode, enableAnimations } = useConfig();

  // Copy of breakpoints from packages/sfinx/src/renderers/content/bullets/bullets.tsx
  const breakpoints = [
    { count: 1600, offset: -6 }, // Handling very content rich lists
    { count: 600, offset: -4 },
    { count: 400, offset: -3 },
    { lineCount: 25, offset: -6 },
    { lineCount: 16, offset: -5 },
    { lineCount: 14, offset: -3 },
    { lineCount: 12, offset: -2 },
    { lineCount: 6, offset: -1 },
  ];
  const maxWidth = choices
    .map((s) => s.label.length)
    .reduce((curr, next) => Math.max(curr, next), 75); // Handle if we have no steps

  const width = hasImage ? Math.min(75, maxWidth) : Math.min(130, maxWidth);
  const height = choices
    .map((s) => s.label.length)
    .reduce(
      (curr, next) => curr + Math.ceil(next / width),
      Math.ceil(title.length / width),
    );
  const offset =
    fontOffset({ width, height, breakpoints }) + (fontSizeOffset ?? 0);

  return (
    <Box
      width="100%"
      alignItems={alignItems}
      justifyContent={justifyContent}
      data-testid="bullets-container"
    >
      <ContentSlideTitleSection
        question={title}
        title_meta={staticContent.label}
        layout={legacyLayout}
        textAlign={textAlign}
        alignItems={alignItems}
        enableAnimations={true}
        justifyContent="flex-end"
        mb="space4"
      />
      <StaggerCss
        flex="auto"
        width="auto"
        index={2}
        type="fade"
        enableAnimations={enableAnimations}
      >
        <Steps
          fontOffset={offset - 1}
          enableAnimations={enableAnimations}
          step={step}
          steps={choices}
          showAllSteps={
            staticContent.listStyling.listDisplayMode === 'simultaneous' ||
            mode === 'screenshot' ||
            mode === 'public'
          }
          numberedListEnabled={
            staticContent.listStyling.listStyle === 'numbered'
          }
        />
      </StaggerCss>
    </Box>
  );
}
